import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEgdHubCards } from "src/hooks/useEgdHubCards";
import { useLocation, useNavigate } from "react-router-dom";
import GameListHero from "src/components/ElectronicGamingDevices/GameListHero";
import TitleSection from "src/components/TitleSection/TitleSection";
import SectionalTabs from "src/components/SectionalTabs/SectionalTabs";
import GameListCard from "src/components/ElectronicGamingDevices/GameListCard";
import { egdHubViewFromTabTitle } from "src/utils/egdHubView";

const EgdGameList = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const routeCategory = "egdGameList";
  const categories = require(`../locales/${i18n.language}/pseudoRoutes.json`);
  const categoryArr = useMemo(
    () => Object.values(categories.pseudoRoutes[routeCategory] || {}),
    [i18n.language]
  );

  const [selectedCategory, setSelectedCategory] = useState({
    title: categoryArr[0]?.title || "",
    path: categoryArr[0]?.path || "",
  });
const updatedPath = location.pathname?.includes(t("routes.egdGameList"));

  const hubView = useMemo(
    () => egdHubViewFromTabTitle(selectedCategory.title, t),
    [selectedCategory.title, t]
  );

  const { cards, loading, error } = useEgdHubCards(hubView, i18n.language);

  useEffect(() => {
    if (
      location.pathname === t("routes.egdGamePreview") ||
      location.pathname.endsWith("/egd-games/preview")
    ) {
      return;
    }

    const currentTab = categoryArr.find((el) => el.path === location.pathname);
    const baseRoute = t("routes.egdGameList");

    if (currentTab) {
      if (currentTab.title !== selectedCategory.title) {
        setSelectedCategory({ path: currentTab.path, title: currentTab.title });
      }
      return;
    }

    if (location.pathname === baseRoute) {
      if (!updatedPath) {
        navigate(t("routes.error"));
        return;
      }
      setSelectedCategory({
        path: categoryArr[0]?.path,
        title: categoryArr[0]?.title,
      });
      return;
    }

    navigate(baseRoute, { replace: true });
  }, [i18n.language, location.pathname]);

  const renderCards = () => (
    <Row className="g-4 gx-3" xs={1} md={2} lg={3}>
      {cards.map(({ game, jackpot }) => (
        <GameListCard
          key={game.game_name}
          game={game}
          jackpot={jackpot || undefined}
        />
      ))}
    </Row>
  );

  const renderTabBody = (titleKey, descriptionKey) => (
    <>
      <TitleSection
        as={"h2"}
        title={t(titleKey)}
        text={t(descriptionKey)}
      />
      {loading ? (
        <SpinnerHolder>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </SpinnerHolder>
      ) : error ? (
        <div className="text-center text-danger">
          <p>
            {t("error.somethingWentWrong")}: {error}
          </p>
        </div>
      ) : (
        renderCards()
      )}
    </>
  );

  return (
    <>
      <JackpotPage>
        <GameListHero />
        <container-wide>
          <SectionalTabs
            className="mt-4 mb-4"
            routeData={{
              route: t(`routes.egdGameList`),
              routeCategory,
              sectionTitle: categoryArr[0]?.title || "",
            }}
            isDynamic={true}
            selectedCategory={selectedCategory.title}
            bgColor="var(--darkmustard)"
            textColor="var(--black)"
            activeBgColor="var(--mustard)"
            activeTextColor="var(--black)"
          />

          <TabContent>
            {selectedCategory.title ===
              t("pseudoRoutes.egdGameList.newAndPopular.title") &&
              renderTabBody(
                "egd.egdGameList.newAndPopular.title",
                "egd.egdGameList.newAndPopular.description"
              )}

            {selectedCategory.title ===
              t("pseudoRoutes.egdGameList.progressiveJackpots.title") &&
              renderTabBody(
                "egd.egdGameList.progressiveJackpots.title",
                "egd.egdGameList.progressiveJackpots.description"
              )}
          </TabContent>
          <Disclaimer className="text-center">
            *{t("egd.egdGameList.progressive_note_1")}
          </Disclaimer>
        </container-wide>
      </JackpotPage>
    </>
  );
};

const JackpotPage = styled.div`
  background-color: var(--black);
`;

const Disclaimer = styled.p`
  color: var(--mustard);
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const TabContent = styled.div`
  background: var(--lightblack);
  padding: 0 1rem 1rem;
  margin-bottom: 2rem;
`;

const SpinnerHolder = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px;
`;

export default EgdGameList;
